import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RecipeCost, RecipeCostDetail, RecipeCostHeader, USDAProductStatusTypeData } from '../../api';
import { RecipeCategory } from '../../api/models/recipe-category.models';
import { RecipeCustomerDiary } from '../../api/models/recipe-customer-diary.models';
import { RecipeCustomerShare } from '../../api/models/recipe-customer-share.models';
import { RecipeCustomerUserManage } from '../../api/models/recipe-customer-user-manage.models';
import { RecipeDetail, RecipeDetailHeader, RecipeDetailType } from '../../api/models/recipe-detail.models';
import { RecipeDiary } from '../../api/models/recipe-diary.models';
import { RecipeHeaderPrivateUser } from '../../api/models/recipe-header-private-user.models';
import { RecipeHeader } from '../../api/models/recipe-header.models';
import { RecipeUom } from '../../api/models/recipe-uom.models';
import { RecipeUser } from '../../api/models/recipe-users.models';
import { RecipeType } from '../../api/models/recipe.enums';
import { filterObjects, getSortComparer, normalizeProductKey } from '../../helpers';
import { RootState } from '../store';

//Adapter
const recipeHeadersAdapter = createEntityAdapter<RecipeHeader>({
  selectId: (data: RecipeHeader) => data.RecipeHeaderId,
  sortComparer: (a: RecipeHeader, b: RecipeHeader) => {
    const compareCategorized = getSortComparer<RecipeHeader, boolean>((r) => r.CategoryName === 'Uncategorized');
    const compareCategoryName = getSortComparer<RecipeHeader, string>((r) => r.CategoryName);
    const compareName = getSortComparer<RecipeHeader, string>((r) => r.RecipeName);
    return compareCategorized(a, b) || compareCategoryName(a, b) || compareName(a, b);
  },
});

const recipeCategoriesAdapter = createEntityAdapter<RecipeCategory>({
  selectId: (data: RecipeCategory) => data.RecipeCategoryId,
  sortComparer: getSortComparer((d) => d.CategoryName),
});

const recipeDetailsAdapter = createEntityAdapter<RecipeDetail>({
  selectId: (data: RecipeDetail) => data.RecipeDetailId,
  sortComparer: getSortComparer((d) => d.Sequence),
});

const recipeDiaryAdapter = createEntityAdapter<RecipeDiary>({
  selectId: (data) => data.RecipeDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

const recipeCustomerDiaryAdapter = createEntityAdapter<RecipeCustomerDiary>({
  selectId: (data) => data.RecipeCustomerDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

const recipeUomAdapter = createEntityAdapter<RecipeUom>({
  selectId: (data) => data.RecipeUnitOfMeasureTypeEnum,
  sortComparer: getSortComparer((d) => d.RecipeUnitOfMeasureTypeEnum),
});

const recipeCustomerShareAdapter = createEntityAdapter<RecipeCustomerShare>({
  selectId: (data: RecipeCustomerShare) => data.RecipeCustomerShareId,
  sortComparer: getSortComparer((d) => d.SharedWithCustomerName),
});

const recipeHeaderPrivateUserAdapter = createEntityAdapter<RecipeHeaderPrivateUser>({
  selectId: (data) => data.RecipeHeaderPrivateUserId,
  sortComparer: getSortComparer((d) => d.UserDisplay),
});

const recipeCustomerUserManageAdapter = createEntityAdapter<RecipeCustomerUserManage>({
  selectId: (data) => data.RecipeCustomerUserManageId,
  sortComparer: getSortComparer((d) => d.UserDisplay),
});

const recipeCostDetailAdapter = createEntityAdapter<RecipeCostDetail>({
  selectId: (data) => data.RecipeDetailId,
});

const recipeUsersAdapter = createEntityAdapter<RecipeUser>({
  selectId: (data) => data.UserId,
});

const subRecipeRecipesAdapter = createEntityAdapter<RecipeDetailHeader>({
  selectId: (data) => data.RecipeDetail.RecipeDetailId,
});

// States
interface RecipesState {
  gridParameters: { customerId: string; menuHeaderId?: string } | undefined;

  isRecipeHeaderLoading: boolean;

  isRecipeHeadersLoading: boolean;
  recipeHeaders: EntityState<RecipeHeader>;

  isRecipesCategoriesLoading: boolean;
  recipeCategories: EntityState<RecipeCategory>;

  isRecipeDetailsLoading: boolean;
  recipeDetails: EntityState<RecipeDetail>;

  isRecipeDiariesLoading: boolean;
  recipeDiaries: EntityState<RecipeDiary>;

  isRecipeCustomerDiariesLoading: boolean;
  recipeCustomerDiaries: EntityState<RecipeCustomerDiary>;

  isRecipeUomsLoading: boolean;
  recipeUoms: EntityState<RecipeUom>;

  isRecipeCustomerSharesLoading: boolean;
  recipeCustomerShares: EntityState<RecipeCustomerShare>;

  isRecipeHeaderPrivateUsersLoading: boolean;
  recipeHeaderPrivateUsers: EntityState<RecipeHeaderPrivateUser>;

  isRecipeCustomerUserManagesLoading: boolean;
  recipeCustomerUserManages: EntityState<RecipeCustomerUserManage>;

  isLoadingRecipeCost: boolean;
  recipeCostHeader: RecipeCostHeader | undefined;
  recipeCostDetails: EntityState<RecipeCostDetail>;

  isLoadingRecipeUsers: boolean;
  recipeUsers: EntityState<RecipeUser>;

  isSubRecipeRecipesLoading: boolean;
  subRecipeRecipes: EntityState<RecipeDetailHeader>;

  isRecipeReportExporting: boolean;
}

const initialState: RecipesState = {
  gridParameters: undefined,

  isRecipeHeaderLoading: false,

  isRecipeHeadersLoading: true,
  recipeHeaders: recipeHeadersAdapter.getInitialState(),

  isRecipesCategoriesLoading: true,
  recipeCategories: recipeCategoriesAdapter.getInitialState(),

  isRecipeDetailsLoading: false,
  recipeDetails: recipeDetailsAdapter.getInitialState(),

  isRecipeDiariesLoading: false,
  recipeDiaries: recipeDiaryAdapter.getInitialState(),

  isRecipeCustomerDiariesLoading: false,
  recipeCustomerDiaries: recipeCustomerDiaryAdapter.getInitialState(),

  isRecipeUomsLoading: false,
  recipeUoms: recipeUomAdapter.getInitialState(),

  isRecipeCustomerSharesLoading: false,
  recipeCustomerShares: recipeCustomerShareAdapter.getInitialState(),

  isRecipeHeaderPrivateUsersLoading: false,
  recipeHeaderPrivateUsers: recipeHeaderPrivateUserAdapter.getInitialState(),

  isRecipeCustomerUserManagesLoading: false,
  recipeCustomerUserManages: recipeCustomerUserManageAdapter.getInitialState(),

  isLoadingRecipeCost: false,
  recipeCostHeader: undefined,
  recipeCostDetails: recipeCostDetailAdapter.getInitialState(),

  isLoadingRecipeUsers: false,
  recipeUsers: recipeUsersAdapter.getInitialState(),

  isSubRecipeRecipesLoading: false,
  subRecipeRecipes: subRecipeRecipesAdapter.getInitialState(),

  isRecipeReportExporting: false,
};

// Reducers
export const recipesSlice = createSlice({
  name: 'recipes',
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
    resetRecipes: (state: RecipesState) => {
      state.gridParameters = initialState.gridParameters;
      state.recipeHeaders = initialState.recipeHeaders;
      state.recipeCategories = initialState.recipeCategories;
    },
    resetRecipeDetails: (state: RecipesState) => {
      state.recipeDetails = initialState.recipeDetails;
    },
    resetManageAccess: (state: RecipesState) => {
      state.recipeUsers = initialState.recipeUsers;
      state.recipeCustomerShares = initialState.recipeCustomerShares;
      state.recipeCustomerUserManages = initialState.recipeCustomerUserManages;
      state.recipeHeaderPrivateUsers = initialState.recipeHeaderPrivateUsers;
    },
    setGridParameters: (
      state: RecipesState,
      action: PayloadAction<{ customerId: string; menuHeaderId?: string } | undefined>
    ) => {
      state.gridParameters = action.payload;
    },
    resetGridParameters: (state: RecipesState) => {
      state.gridParameters = initialState.gridParameters;
    },
    setIsLoadingRecipeHeader: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeHeadersLoading = action.payload;
    },
    setRecipeHeader: (state: RecipesState, action: PayloadAction<RecipeHeader | undefined>) => {
      if (action.payload) recipeHeadersAdapter.setOne(state.recipeHeaders, action.payload);
    },
    setIsLoadingRecipeHeaders: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeHeadersLoading = action.payload;
    },
    setRecipeHeaders: (state: RecipesState, action: PayloadAction<RecipeHeader[]>) => {
      recipeHeadersAdapter.setAll(state.recipeHeaders, action.payload);
    },
    removeRecipeHeader: (state: RecipesState, action: PayloadAction<string>) => {
      recipeHeadersAdapter.removeOne(state.recipeHeaders, action.payload);
    },
    setIsLoadingRecipeCategories: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipesCategoriesLoading = action.payload;
    },
    setRecipeCategories: (state: RecipesState, action: PayloadAction<RecipeCategory[]>) => {
      recipeCategoriesAdapter.setAll(state.recipeCategories, action.payload);
    },
    setRecipeCategory: (state: RecipesState, action: PayloadAction<RecipeCategory>) => {
      recipeCategoriesAdapter.setOne(state.recipeCategories, action.payload);
    },
    removeRecipeCategory: (state: RecipesState, action: PayloadAction<string>) => {
      recipeCategoriesAdapter.removeOne(state.recipeCategories, action.payload);
    },
    updateCategoryIsCollapsed: (
      state: RecipesState,
      action: PayloadAction<{ isCollapsed: boolean; categoryId: string }>
    ) => {
      const recipes = Object.values(state.recipeHeaders.entities);
      recipes.forEach((c) => {
        if (c) {
          if (c.RecipeCategoryId === action.payload.categoryId) c.IsCollapsed = action.payload.isCollapsed;
        }
      });
    },
    updateAllCategoryIsCollapsed: (state: RecipesState, action: PayloadAction<boolean>) => {
      const recipes = Object.values(state.recipeHeaders.entities);
      recipes.forEach((c) => {
        if (c) {
          c.IsCollapsed = action.payload;
        }
      });
    },

    updateIsHidden: (state: RecipesState, action: PayloadAction<{ isHidden: boolean; recipeHeaderId: string }>) => {
      const recipe = state.recipeHeaders.entities[action.payload.recipeHeaderId];
      if (recipe) recipe.IsHidden = action.payload.isHidden;
    },
    updateIsPrivate: (state: RecipesState, action: PayloadAction<{ isPrivate: boolean; recipeHeaderId: string }>) => {
      const recipe = state.recipeHeaders.entities[action.payload.recipeHeaderId];
      if (recipe) recipe.IsPrivate = action.payload.isPrivate;
    },
    setIsLoadingRecipeDetails: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeDetailsLoading = action.payload;
    },
    addRecipeDetails: (state: RecipesState, action: PayloadAction<RecipeDetail[]>) => {
      recipeDetailsAdapter.addMany(state.recipeDetails, action.payload);
    },
    setRecipeDetails: (state: RecipesState, action: PayloadAction<RecipeDetail[]>) => {
      recipeDetailsAdapter.setAll(state.recipeDetails, action.payload);
    },
    setRecipeDetail: (state: RecipesState, action: PayloadAction<RecipeDetail>) => {
      recipeDetailsAdapter.setOne(state.recipeDetails, action.payload);
    },
    removeRecipeDetail: (state: RecipesState, action: PayloadAction<string>) => {
      recipeDetailsAdapter.removeOne(state.recipeDetails, action.payload);
    },
    setIsRecipeDiariesLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeDiariesLoading = action.payload;
    },
    setRecipeDiaries: (state: RecipesState, action: PayloadAction<RecipeDiary[]>) => {
      recipeDiaryAdapter.setAll(state.recipeDiaries, action.payload);
    },
    setIsRecipeCustomerDiariesLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeCustomerDiariesLoading = action.payload;
    },
    setRecipeCustomerDiaries: (state: RecipesState, action: PayloadAction<RecipeCustomerDiary[]>) => {
      recipeCustomerDiaryAdapter.setAll(state.recipeCustomerDiaries, action.payload);
    },
    removeRecipeCustomerDiary: (state: RecipesState, action: PayloadAction<string>) => {
      recipeCustomerDiaryAdapter.removeOne(state.recipeCustomerDiaries, action.payload);
    },
    setIsRecipeUomsLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeUomsLoading = action.payload;
    },
    setRecipeUoms: (state: RecipesState, action: PayloadAction<RecipeUom[]>) => {
      recipeUomAdapter.setAll(state.recipeUoms, action.payload);
    },
    setIsLoadingRecipeCustomerShares: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeCustomerSharesLoading = action.payload;
    },
    setRecipeCustomerShares: (state: RecipesState, action: PayloadAction<RecipeCustomerShare[]>) => {
      recipeCustomerShareAdapter.setAll(state.recipeCustomerShares, action.payload);
    },
    setRecipeCustomerShare: (state: RecipesState, action: PayloadAction<RecipeCustomerShare>) => {
      recipeCustomerShareAdapter.setOne(state.recipeCustomerShares, action.payload);
    },
    setIsRecipeHeaderPrivateUsersLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeHeaderPrivateUsersLoading = action.payload;
    },
    setRecipeHeaderPrivateUsers: (state: RecipesState, action: PayloadAction<RecipeHeaderPrivateUser[]>) => {
      recipeHeaderPrivateUserAdapter.setAll(state.recipeHeaderPrivateUsers, action.payload);
    },
    setManyRecipeHeaderPrivateUsers: (
      state: RecipesState,
      action: PayloadAction<{ data: RecipeHeaderPrivateUser[]; recipeHeaderId: string }>
    ) => {
      const ids = selectAllRecipeHeaderPrivateUsers(state.recipeHeaderPrivateUsers)
        .filter((x) => x && x?.RecipeHeaderId === action.payload.recipeHeaderId)
        .map((x) => x?.RecipeHeaderPrivateUserId);
      recipeHeaderPrivateUserAdapter.removeMany(state.recipeHeaderPrivateUsers, ids);
      recipeHeaderPrivateUserAdapter.setMany(state.recipeHeaderPrivateUsers, action.payload.data);
    },
    setIsRecipeCustomerUserManagesLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeCustomerUserManagesLoading = action.payload;
    },
    setRecipeCustomerUserManages: (state: RecipesState, action: PayloadAction<RecipeCustomerUserManage[]>) => {
      recipeCustomerUserManageAdapter.setAll(state.recipeCustomerUserManages, action.payload);
    },
    setIsLoadingRecipeCost: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isLoadingRecipeCost = action.payload;
    },
    setRecipeCost: (state: RecipesState, action: PayloadAction<RecipeCost>) => {
      state.recipeCostHeader = action.payload.RecipeCostHeader;
      recipeCostDetailAdapter.setAll(state.recipeCostDetails, action.payload.RecipeCostDetails);
    },
    setIsRecipeUsersLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isLoadingRecipeUsers = action.payload;
    },
    setRecipeUsers: (state: RecipesState, action: PayloadAction<RecipeUser[]>) => {
      recipeUsersAdapter.setAll(state.recipeUsers, action.payload);
    },
    setSubRecipeRecipes: (state: RecipesState, action: PayloadAction<RecipeDetailHeader[]>) => {
      subRecipeRecipesAdapter.setAll(state.subRecipeRecipes, action.payload);
    },
    setIsSubRecipeRecipesLoading: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isSubRecipeRecipesLoading = action.payload;
    },
    setRecipeHeaderIsOnMenu: (
      state: RecipesState,
      action: PayloadAction<{ isOnMenu: boolean; recipeHeaderId: string }>
    ) => {
      const recipe = state.recipeHeaders.entities[action.payload.recipeHeaderId];
      if (recipe) recipe.IsOnMenu = action.payload.isOnMenu;
    },
    setRecipeHeadersIsOnMenu: (
      state: RecipesState,
      action: PayloadAction<{ isOnMenu: boolean; recipeHeaderIds: string[] }>
    ) => {
      const recipes = Object.values(state.recipeHeaders.entities);
      const headerset = new Set(action.payload.recipeHeaderIds);
      recipes.forEach((c) => {
        if (c && headerset.has(c.RecipeHeaderId)) c.IsOnMenu = action.payload.isOnMenu;
      });
    },
    setRecipeHeadersIsPrivate: (
      state: RecipesState,
      action: PayloadAction<{ isPrivate: boolean; recipeHeaderId: string }[]>
    ) => {
      action.payload.forEach((x) => {
        const item = state.recipeHeaders.entities?.[x.recipeHeaderId];
        if (item) {
          item.IsPrivate = x.isPrivate;
        }
      });
    },
    setRecipeHeadersIsHidden: (
      state: RecipesState,
      action: PayloadAction<{ isHidden: boolean; recipeHeaderId: string }[]>
    ) => {
      action.payload.forEach((x) => {
        const item = state.recipeHeaders.entities?.[x.recipeHeaderId];
        if (item) {
          item.IsHidden = x.isHidden;
        }
      });
    },
    setRecipeReportIsExporting: (state: RecipesState, action: PayloadAction<boolean>) => {
      state.isRecipeReportExporting = action.payload;
    },
    setRecipeHeadersOrderServings: (state: RecipesState, action: PayloadAction<number | undefined>) => {
      const recipes = Object.values(state.recipeHeaders.entities);
      recipes.forEach((c) => {
        if (c) c.OrderServings = action.payload;
      });
    },
    setRecipeHeaderOrderServings: (
      state: RecipesState,
      action: PayloadAction<{ orderServings: number; recipeHeaderIds: string[] }>
    ) => {
      const recipes = Object.values(state.recipeHeaders.entities);
      const headerset = new Set(action.payload.recipeHeaderIds);
      recipes.forEach((c) => {
        if (c && headerset.has(c.RecipeHeaderId)) c.OrderServings = action.payload.orderServings;
      });
    },
    setUsdaProductStatusType: (state: RecipesState, action: PayloadAction<USDAProductStatusTypeData[]>) => {
      action.payload.forEach((statusResult: USDAProductStatusTypeData) => {
        if (statusResult.ProductKey) {
          Object.values(state.recipeCostDetails.entities).forEach((recipeCostDetail) => {
            if (
              normalizeProductKey(recipeCostDetail?.RecipeIngredientProduct?.Product?.ProductKey) ==
              normalizeProductKey(statusResult.ProductKey)
            ) {
              const product = recipeCostDetail?.RecipeIngredientProduct?.Product;
              if (product) {
                product.USDAProductStatusType = statusResult.USDAProductStatusType;
              }
            }
          });
        }
      });
    },
  },
});

export const { selectAll: selectAllRecipeHeaders, selectById: selectAllRecipeHeaderById } =
  recipeHeadersAdapter.getSelectors<EntityState<RecipeHeader>>((state: EntityState<RecipeHeader>) => state);

export const { selectAll: selectAllRecipeCategories, selectById: selectRecipeCategoryById } =
  recipeCategoriesAdapter.getSelectors<EntityState<RecipeCategory>>((state: EntityState<RecipeCategory>) => state);

export const {
  selectAll: selectAllRecipeDetails,
  selectIds: selectAllRecipeDetailIds,
  selectById: selectRecipeDetailById,
} = recipeDetailsAdapter.getSelectors<EntityState<RecipeDetail>>((state: EntityState<RecipeDetail>) => state);

export const { selectAll: selectAllRecipeDiaries } = recipeDiaryAdapter.getSelectors(
  (state: EntityState<RecipeDiary>) => state
);

export const { selectAll: selectAllRecipeCustomerDiaries } = recipeCustomerDiaryAdapter.getSelectors(
  (state: EntityState<RecipeCustomerDiary>) => state
);

export const { selectAll: selectAllRecipeUoms } = recipeUomAdapter.getSelectors();

export const { selectAll: selectAllRecipeCustomerShares } = recipeCustomerShareAdapter.getSelectors();

export const { selectAll: selectAllRecipeHeaderPrivateUsers } = recipeHeaderPrivateUserAdapter.getSelectors();

export const { selectAll: selectAllRecipeCustomerUserManages, selectById: selectRecipeCustomerUserManageById } =
  recipeCustomerUserManageAdapter.getSelectors();
export const { selectAll: selectAllRecipeUsers } = recipeUsersAdapter.getSelectors();

export const selectAllRecipesCategoriesCollapsed = createSelector(selectAllRecipeHeaders, (recipes) => {
  if (recipes.length === 0) return false;
  return recipes.filter((c) => !c.IsCollapsed).length === 0;
});

const getRecipeHeaders = (state: RootState): EntityState<RecipeHeader> => state.recipes.recipeHeaders;
const getFilterText = (_s: RootState, filterText: string | undefined) => filterText;
const getView = (_s: RootState, _ft: string | undefined, view: RecipeType | undefined) => view;
const getCategoryId = (
  _s: RootState,
  _ft: string | undefined,
  _v: RecipeType | undefined,
  categoryId: string | undefined
) => categoryId;
const getFilterIsOnMenu = (
  _s: RootState,
  _ft: string | undefined,
  _v: RecipeType | undefined,
  _c: string | undefined,
  filterIsOnMenu: boolean | undefined
) => filterIsOnMenu;
const getIgnoreCollapsed = (
  _s: RootState,
  _ft: string | undefined,
  _v: RecipeType | undefined,
  _c: string | undefined,
  _fm: boolean | undefined,
  ignoreCollapsed: boolean | undefined
) => ignoreCollapsed;

const getFilterIsEditable = (
  _s: RootState,
  _ft: string | undefined,
  _v: RecipeType | undefined,
  _c: string | undefined,
  _fm: boolean | undefined,
  _ic: boolean | undefined,
  filterIsEditable: boolean | undefined
) => filterIsEditable;

const getFilterCanViewDetails = (
  _s: RootState,
  _ft: string | undefined,
  _v: RecipeType | undefined,
  _c: string | undefined,
  _fm: boolean | undefined,
  _ic: boolean | undefined,
  _fe: boolean | undefined,
  filterCanViewDetails: boolean | undefined
) => filterCanViewDetails;

export const selectRecipesGridData = createSelector(
  [
    getRecipeHeaders,
    getFilterText,
    getView,
    getCategoryId,
    getFilterIsOnMenu,
    getIgnoreCollapsed,
    getFilterIsEditable,
    getFilterCanViewDetails,
  ],
  (
    recipeHeaders,
    filterText,
    view,
    categoryId,
    filterIsOnMenu,
    ignoreCollapsed,
    filterIsEditable,
    filterCanViewDetails
  ) => {
    const collapsedCategoryIds = new Set<string | undefined>();
    const result = filterRecipeHeaders(selectAllRecipeHeaders(recipeHeaders), filterText).filter((d) => {
      let include = true;
      if (view !== undefined) {
        if (view === RecipeType.IsPrivate && !d.IsPrivate) {
          include = false;
        } else if (view === RecipeType.IsHidden && !d.IsHidden) {
          include = false;
        } else if (view !== RecipeType.IsPrivate && view !== RecipeType.IsHidden && d.RecipeType !== view) {
          include = false;
        }
      }
      if (categoryId !== undefined && d.RecipeCategoryId !== categoryId) {
        include = false;
      }
      if (filterIsOnMenu && !d.IsOnMenu) {
        include = false;
      }

      if (filterIsEditable !== undefined && !d.IsEditable) {
        include = false;
      }

      if (filterCanViewDetails !== undefined && !d.CanViewDetails) {
        include = false;
      }

      if (include && !ignoreCollapsed) {
        if (!collapsedCategoryIds.has(d.RecipeCategoryId)) {
          collapsedCategoryIds.add(d.RecipeCategoryId);
        } else if (d.IsCollapsed) {
          include = false;
        }
      }
      return include;
    });

    return result;
  }
);

const filterRecipeHeaders = (recipeHeaders: RecipeHeader[], filterText?: string): RecipeHeader[] => {
  return filterObjects(
    recipeHeaders,
    filterText || '',
    ['CategoryName', 'RecipeName', 'RecipeNumber', 'RecipeType', 'Tags', 'IsPrivate', 'IsHidden'],
    [
      {
        field: 'RecipeNumber',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          !!(val && val.toLowerCase().includes(queryString.toLowerCase())),
      },
      {
        field: 'RecipeType',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          (val.toLowerCase() === '0' && 'recipes'.includes(queryString)) ||
          (val.toLowerCase() === '1' && 'sub-recipes'.includes(queryString)) ||
          (val.toLowerCase() === '2' && 'prep item'.includes(queryString)),
      },
      {
        field: 'Tags',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          !!(val && val.toLowerCase().includes(queryString.toLowerCase())),
      },
      {
        field: 'IsPrivate',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          val.toLowerCase() === 'true' && 'secret'.includes(queryString),
      },
      {
        field: 'IsHidden',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          val.toLowerCase() === 'true' && 'hidden'.includes(queryString),
      },
    ]
  );
};

export const selectBrowseRecipesGridData = createSelector([getRecipeHeaders], (recipeHeaders) => {
  const cateogryIds = new Set<string | undefined>();
  const result = selectAllRecipeHeaders(recipeHeaders).filter((d) => {
    let include = false;
    if (!cateogryIds.has(d.RecipeCategoryId)) {
      cateogryIds.add(d.RecipeCategoryId);
      include = true;
    }
    return include;
  });
  return result;
});

const getBrowseCategoryId = (_s: RootState, categoryId: string | undefined) => categoryId;

export const selectBrowseRecipeCategoryName = createSelector(
  [getRecipeHeaders, getBrowseCategoryId],
  (recipeHeaders, categoryId) => {
    const result = selectAllRecipeHeaders(recipeHeaders).find((u) => u.RecipeCategoryId === categoryId);
    return result?.CategoryName;
  }
);

const getRecipeCategoryId = (_s: RootState, id: EntityId) => id;
const getFilterIsOnMenuCategory = (_s: RootState, id: EntityId, filterIsOnMenu: boolean | undefined) => filterIsOnMenu;

export const selectRecipeCategoryRecipeCount = createSelector(
  [(state: RootState) => state.recipes.recipeHeaders, getRecipeCategoryId, getFilterIsOnMenuCategory],
  (recipes, categoryId, filterIsOnMenu) => {
    const headers = selectAllRecipeHeaders(recipes);
    const result = headers.filter(
      (d) => d.RecipeCategoryId === categoryId && (!filterIsOnMenu || (filterIsOnMenu && d.IsOnMenu))
    ).length;
    return result;
  }
);

const getRecipeDiary = (state: RootState): EntityState<RecipeDiary> => state.recipes.recipeDiaries;

const filterRecipeDiary = (recipeDiary: RecipeDiary[], filterText?: string): RecipeDiary[] => {
  return filterObjects(recipeDiary, filterText || '', ['ActionType', 'ActionSummary', 'ChangeByUser']);
};

export const selectRecipeDiary = createSelector([getRecipeDiary, getFilterText], (recipeDiary, filterText) => {
  return filterRecipeDiary(selectAllRecipeDiaries(recipeDiary), filterText);
});

const getRecipeCustomerDiary = (state: RootState): EntityState<RecipeCustomerDiary> =>
  state.recipes.recipeCustomerDiaries;

const filterRecipeCustomerDiary = (recipeDiary: RecipeCustomerDiary[], filterText?: string): RecipeCustomerDiary[] => {
  return filterObjects(recipeDiary, filterText || '', ['ActionType', 'ActionSummary', 'ChangeByUser']);
};

export const selectRecipeCustomerDiary = createSelector(
  [getRecipeCustomerDiary, getFilterText],
  (recipeDiary, filterText) => {
    return filterRecipeCustomerDiary(selectAllRecipeCustomerDiaries(recipeDiary), filterText);
  }
);

export const selectCustomerRecipeCategories = createSelector([selectAllRecipeCategories], (recipeCategories) => {
  return recipeCategories.sort((a, b) => a.CategoryName.localeCompare(b.CategoryName));
});

export const {
  selectAll: selectAllRecipeCostDetails,
  selectIds: selectAllRecipeCostDetailIds,
  selectById: selectRecipeCostDetailById,
} = recipeCostDetailAdapter.getSelectors<EntityState<RecipeCostDetail>>(
  (state: EntityState<RecipeCostDetail>) => state
);

const getIsRecipeDetailsLoading = (state: RootState) => state.recipes.isRecipeDetailsLoading;
const getRecipeDetails = (state: RootState) => selectAllRecipeDetails(state.recipes.recipeDetails);

export const selectHasSubrecipes = createSelector(
  [getRecipeDetails, getIsRecipeDetailsLoading],
  (details, isLoading) => {
    return !isLoading && details.filter((o) => o.RecipeDetailTypeEnum === RecipeDetailType.SubRecipeHeader).length > 0;
  }
);

export const selectRecipeDetailCheckable = createSelector([getRecipeDetails], (details) => {
  return details.filter(isRecipeDetailCheckable);
});

export const selectRecipeCanFetchCosting = createSelector([getRecipeDetails], (details) => {
  return details.length > 0 && details.filter(isRecipeDetailCheckable).length !== details.length;
});

export const { selectAll: selectAllSubRecipeRecipes } = subRecipeRecipesAdapter.getSelectors();

export const isRecipeDetailCheckable = (o: RecipeDetail) =>
  o.RecipeDetailTypeEnum !== RecipeDetailType.SubRecipeHeader && !o.IsGenericIngredient && !o.HasProductLink;

export const selectRecipeOrderServings = createSelector([getRecipeHeaders], (recipeHeaders) => {
  const result = selectAllRecipeHeaders(recipeHeaders)
    .filter((d) => d.IsOnMenu)
    .map((x) => ({ Servings: x.OrderServings ?? x.Servings, RecipeHeaderId: x.RecipeHeaderId }));

  return result;
});

export const selectRecipeRecentlyEdited = createSelector([selectAllRecipeHeaders], (headers) => {
  const result = headers
    .filter((d) => d.IsEditable && d.CanViewDetails)
    .sort((a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime())
    .splice(0, 4);

  return result;
});

const getShares = (state: RootState) => selectAllRecipeCustomerShares(state.recipes.recipeCustomerShares);

export const selectAllRecipeCustomerShareData = createSelector([getShares, getFilterText], (shares, filterText) => {
  let result = shares;
  if (filterText)
    result = shares.filter((location) =>
      Object.values(location).some((value) => value?.toString().toLowerCase().includes(filterText.toLowerCase()))
    );
  return result;
  //return filterObjects(shares, filterText || '', [''])
});

const getSharedManageUsers = (state: RootState) =>
  selectAllRecipeCustomerUserManages(state.recipes.recipeCustomerUserManages);

export const selectAllRecipeCustomerUserManageData = createSelector(
  [getSharedManageUsers, getFilterText],
  (users, filterText) => {
    let result = users;
    if (filterText)
      result = users.filter((location) =>
        Object.values(location).some((value) => value?.toString().toLowerCase().includes(filterText.toLowerCase()))
      );
    return result;
    //return filterObjects(shares, filterText || '', [''])
  }
);

selectAllRecipeHeaderPrivateUsers;

const getPrivateUsers = (state: RootState) => selectAllRecipeHeaderPrivateUsers(state.recipes.recipeHeaderPrivateUsers);
const getRecipeHeaderId = (_s: RootState, recipeId: string) => recipeId;

export const selectAllRecipeHeaderPrivateUsersData = createSelector(
  [getPrivateUsers, getRecipeHeaderId],
  (items, recipeId) => {
    return items.filter((item) => item.RecipeHeaderId === recipeId).sort(getSortComparer((a) => a.UserDisplay));
  }
);

export const selectSecretRecipesGridData = createSelector(
  [getRecipeHeaders, getPrivateUsers, getFilterText],
  (recipeHeaders, privateUsers, filterText) => {
    const searchParam = filterText ?? '';
    const result = selectAllRecipeHeaders(recipeHeaders).filter((recipe) => {
      if (!recipe.IsPrivate || !recipe.CanViewDetails) {
        return false;
      }

      let include = Object.values(recipe || {}).some((value) =>
        value?.toString().toLowerCase().includes(searchParam.toLowerCase())
      );

      if (!include) {
        include = privateUsers.some(
          (item) =>
            item.RecipeHeaderId === recipe.RecipeHeaderId &&
            item.UserDisplay?.toLowerCase().includes(searchParam.toLowerCase())
        );
      }

      return include;
    });

    return result;
  }
);

export const selectHiddenRecipesGridData = createSelector(
  [getRecipeHeaders, getFilterText],
  (recipeHeaders, filterText) => {
    const searchParam = filterText ?? '';
    const result = selectAllRecipeHeaders(recipeHeaders).filter((recipe) => {
      if (!recipe.IsHidden || !recipe.IsEditable) {
        return false;
      }

      let include = Object.values(recipe || {}).some((value) =>
        value?.toString().toLowerCase().includes(searchParam.toLowerCase())
      );

      return include;
    });

    return result;
  }
);

export const selectRecipeDetailUsdaProducts = createSelector([selectAllRecipeCostDetails], (details) => {
  return details
    .filter((cd) => cd.RecipeIngredientProduct?.Product?.IsUSDAProduct)
    .map((cd) => cd.RecipeIngredientProduct?.Product?.ProductKey ?? '');
});
